import * as React from "react";
import {
  ComboBox,
  ComboBoxFilterChangeEvent,
  ComboBoxProps,
} from "@progress/kendo-react-dropdowns";
import { FieldRenderProps, FieldWrapper } from "@progress/kendo-react-form";
import { FilterDescriptor, filterBy } from "@progress/kendo-data-query";
import { Error, Hint, Label } from "@progress/kendo-react-labels";
import { CgDanger } from "react-icons/cg";
import { useAppDispatch } from "../../app/hooks";
import { IDrawerState, opened } from "../muidrawer/muiDrawerSlice";
import { DRAWER_WIDTH, INSERT_ACCESS } from "../../_contstants/common";
import { checkAcessRights } from "../../_helper/helper";
const delay = 300;

const FormSelectionField = (
  fieldRenderProps: FieldRenderProps & ComboBoxProps
) => {
  const {
    validationMessage,
    touched,
    label,
    id,
    valid,
    disabled,
    hint,
    value,
    onChange,
    options,
    isAddNew,
    drawerName,
    isIndirect,
    dispatcherFunction,
    addNewLink,
    textField = "label",
    wrapperStyle,
    wrapperClassName,
    astrike = false,
    name,
    ...others
  } = fieldRenderProps;
  const editorRef = React.useRef<any>(null);
  const showValidationMessage: string | false | null =
    touched && validationMessage;
  const showHint: boolean = !showValidationMessage && hint;
  const hintId: string = showHint ? `${id}_hint` : "";
  const errorId: string = showValidationMessage ? `${id}_error` : "";
  const labelId: string = label ? `${id}_label` : "";
  const dispatch = useAppDispatch();

  const [data, setData] = React.useState(options);
  const [loading, setLoading] = React.useState(false);
  // eslint-disable-next-line no-undef
  const timeout = React.useRef<NodeJS.Timeout | null>(null);

  const path = drawerName
    ? drawerName.replace(/form/i, "")
    : addNewLink
    ? addNewLink.replace(/\/create/i, "")
    : "";
  const isAddAccess = checkAcessRights(`/${path}`, INSERT_ACCESS);

  React.useEffect(() => {
    let updatedData = options || [];
    if (isAddNew && isAddAccess) {
      updatedData = [{ label: "Add New", value: "addnew" }, ...updatedData];
    }

    if (dispatcherFunction && isAddAccess) {
      updatedData = [
        { label: "Refresh List", value: "refresh" },
        ...updatedData,
      ];
    }

    setData(updatedData);
  }, [options, isAddNew, dispatcherFunction]);

  const filterData = (filter: FilterDescriptor) => {
    let updatedData = options || [];
    if (isAddNew && isAddAccess) {
      updatedData = [{ label: "Add New", value: "addnew" }, ...updatedData];
    }

    if (dispatcherFunction && isAddAccess) {
      updatedData = [
        { label: "Refresh List", value: "refresh" },
        ...updatedData,
      ];
    }
    const data = updatedData?.slice();
    return filterBy(data, filter);
  };

  const filterChange = (event: ComboBoxFilterChangeEvent) => {
    if (timeout.current) {
      clearTimeout(timeout.current);
    }
    timeout.current = setTimeout(() => {
      setData(filterData(event.filter));
      setLoading(false);
    }, delay);

    setLoading(true);
  };

  const handleSelect = (event: any) => {
    const selectedValue = event.target.value;
    if (selectedValue?.value === "addnew") {
      setData(
        filterData({
          field: "label",
          operator: "contains",
          ignoreCase: true,
          value: "",
        })
      );
      if (addNewLink) {
        window.open(`${location.origin}/${addNewLink}`);
      } else {
        const drawerOptions: IDrawerState = {
          open: true,
          anchor: "right",
          name: drawerName,
          drawerWidth: DRAWER_WIDTH,
          isIndirect: isIndirect,
        };
        dispatch(opened(drawerOptions));
      }
    } else if (selectedValue?.value === "refresh" && dispatcherFunction) {
      dispatch(dispatcherFunction());
    } else {
      onChange(selectedValue);
    }
  };

  let initialValue = options?.find((option: any) => option.value === value);

  return (
    <>
      <FieldWrapper
        style={wrapperStyle}
        className={`position-relative ${wrapperClassName}`}
      >
        <Label editorId={id} editorValid={valid} editorDisabled={disabled}>
          {label}   {astrike && <span style={{ color: "red" }}>*</span>}
        </Label>
        <ComboBox
          ariaLabelledBy={labelId}
          size={"large"}
          ariaDescribedBy={`${hintId} ${errorId}`}
          ref={editorRef}
          valid={valid}
          id={id}
          disabled={disabled}
          {...others}
          data={data}
          name={name}
          value={initialValue || null}
          filterable={true}
          onFilterChange={filterChange}
          loading={loading}
          textField={textField}
          defaultValue={value}
          onChange={handleSelect}
        />
        {showHint && <Hint id={hintId}>{hint}</Hint>}
        {showValidationMessage && (
          <Error className="d-flex justify-content-end" id={errorId}>
            <span
              className="d-flex align-items-center"
              style={{
                background: "#DF4957",
                color: "white",
                padding: "1px 8px 1px 5px",
                borderRadius: 10,
                gap: 5,
              }}
            >
              <CgDanger style={{ marginBottom: 2 }} /> {validationMessage}
            </span>
          </Error>
        )}
      </FieldWrapper>
    </>
  );
};

export default FormSelectionField;
